import {
  Box,
  Button,
  Container,
  ContentLayout,
  FormField,
  Header,
  SpaceBetween,
  Select,
  Checkbox,
  ColumnLayout,
  TokenGroup,
  Link,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BusinessUnitListDropdown from 'src/components/businessUnit/BusinessUnitListDropdown';
import { DEFAULT_LANGUAGE, LANGUAGE_LIST } from 'src/contstant/commonLabels';
import useUserProfile from 'src/hooks/useUserProfile';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useLocations } from 'src/store/locations/useLocations';
import { useUserPreferences } from 'src/store/userPreferences/useUserPreferences';
import { Location } from 'src/types/helios/Location';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { UpdateUserPreferencesRequest } from 'src/types/orbit/UserPreferences';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { ProfileSettingsValidator } from 'src/validations/ProfileSettingsValidator';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import RequiredFieldLabel from 'src/components/RequiredFieldLabel';

import LocationSelectionPanel from 'src/components/location/LocationSelectionPanel';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { transformCodesToLocations, extractCodes } from 'src/helper/locationHelper';
import logoSrc from '../assets/logo.png';
import { useQueryClient } from '@tanstack/react-query';

const logoStyles = {
  height: '100px',
  width: 'auto',
};

interface ProfileSettingsParams {
  businessUnit?: string;
  locations: string[];
  language?: string;
  taskAlert: boolean;
  auditAlert: boolean;
  slackAlert: boolean;
  emailAlert: boolean;
}

const ProfilePreferencesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { businessUnitList, fetchBusinessUnitApiStatus, loadBusinessUnits } =
    useBusinessUnit();
  const {
    userPreferences,
    updateUserPreferencesApiStatus,
    getUserPreferencesApiStatus,
    linkSlackIdentityApiStatus,
    loadUserPreferences,
    updateUserPreferences,
    linkSlackIdentity,
    clearAllUserPreferencesApiMessages,
    resetUpdateStatus,
  } = useUserPreferences();
  const { enableSlackNotifications } = useFeatureFlags();
  const { getUserProgramId, getUserId, getUserName, selectUserLanguage } =
    useUserProfile();
  const { locationsList, loadLocations } = useLocations();

  const { setContent, setTitle, closeView, isOpen } = useSplitPanel();

  const initValues: ProfileSettingsParams = {
    businessUnit: undefined,
    locations: [],
    language: DEFAULT_LANGUAGE,
    taskAlert: true,
    auditAlert: true,
    slackAlert: true,
    emailAlert: true,
  };

  const [profileParams, setProfileParams] = useState<ProfileSettingsParams>(initValues);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Tracks whether the user has interacted with the Slack opt-in checkbox so
  // preference re-hydrations don't overwrite their unsaved choice.
  const slackAlertTouchedRef = useRef(false);
  // Tracks whether any Slack identity link was attempted this visit so the
  // self-heal effect never double-fires alongside an explicit attempt.
  const linkAttemptedRef = useRef(false);
  // Whether link status is surfaced in the flash bar. Only user-initiated
  // link attempts (toggle or save) set this — the automatic self-heal stays
  // silent so users aren't shown errors for actions they never took. Reset
  // when the flash bar messages are cleared.
  const [surfaceLinkStatus, setSurfaceLinkStatus] = useState(false);
  const [requiredBusinessUnitId, setRequiredBusinessUnitId] = useState<string | null>(
    null,
  );
  const [returnUrl, setReturnUrl] = useState<string | null>(null);

  const { fieldValidationError, validate, setFieldValidationErrors } = useFormValidation(
    profileParams,
    ProfileSettingsValidator(locationsList.length),
  );

  // Check for business unit switching context from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const switchToParam = params.get('switchTo');
    const returnUrlParam = params.get('returnUrl');

    if (switchToParam) {
      setRequiredBusinessUnitId(switchToParam);
    }
    if (returnUrlParam) {
      setReturnUrl(returnUrlParam);
    }
  }, []);

  // Load initial data - always load fresh preferences like the modal does
  useEffect(() => {
    // Reset success status to prevent immediate navigation
    resetUpdateStatus();

    loadBusinessUnits({
      programId: getUserProgramId(),
      isActive: true,
      isDeleted: false,
    });
    loadUserPreferences({ programId: getUserProgramId(), userId: getUserId() });

    return () => {
      closeView();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only initial load
  }, []);

  useEffect(() => {
    if (profileParams.businessUnit) {
      // Only close panel on user-initiated business unit changes, not initial load
      if (!isInitialLoad && isOpen) {
        closeView();
      }

      loadLocations({
        isEnabledForBU: true,
        businessUnitId: profileParams.businessUnit,
      });

      const currentBUPrefs =
        userPreferences?.profileSettings?.businessUnitPreferences?.find(
          (pref) => pref.businessUnit === profileParams?.businessUnit,
        );

      setProfileParams((prevState) => ({
        ...prevState,
        locations: currentBUPrefs?.locations || [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react only to user-initiated BU changes
  }, [profileParams.businessUnit]);

  // Update split panel content when locations change, but only if panel is already open
  // and not during initial load
  useEffect(() => {
    if (isOpen && !isInitialLoad) {
      setTitle('Select Locations');
      setContent(createLocationPanel());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh panel content only when its data changes
  }, [locationsList, profileParams.locations]);

  // Update form state when user preferences are loaded
  useEffect(() => {
    if (userPreferences) {
      const { profileSettings, notificationSettings } = userPreferences;

      // Don't auto-select the required BU - let user choose it manually
      const currentBUPrefs = profileSettings?.businessUnitPreferences?.find(
        (pref) => pref.businessUnit === profileSettings?.businessUnit,
      );

      setProfileParams((prevState) => ({
        ...prevState,
        businessUnit: profileSettings?.businessUnit || prevState.businessUnit,
        locations: currentBUPrefs?.locations || [],
        language: profileSettings?.language || DEFAULT_LANGUAGE,
        taskAlert: notificationSettings?.alertMeAbout?.task || false,
        auditAlert: notificationSettings?.alertMeAbout?.audit || false,
        // Hydrate the Slack opt-in from server truth until the user touches
        // the checkbox; afterwards preference refreshes (e.g. a successful
        // identity link merging slackIdentity) must not clobber their
        // unsaved checkbox state.
        slackAlert: slackAlertTouchedRef.current
          ? prevState.slackAlert
          : notificationSettings?.alertCenter?.slack || false,
        emailAlert: notificationSettings?.alertCenter?.email || false,
      }));

      // Mark initial load as complete after preferences are loaded
      setIsInitialLoad(false);
    }
  }, [userPreferences]);

  const hasNoPreferences = !userPreferences?.profileSettings?.businessUnit;

  // Reset form when API call is successful
  useEffect(() => {
    if (updateUserPreferencesApiStatus.isSuccess) {
      // Invalidate all queries to refetch with new business unit
      queryClient.invalidateQueries();
      clearAllUserPreferencesApiMessages();
      resetUpdateStatus();
      // Navigate to returnUrl if provided, otherwise go to home
      navigate(returnUrl || '/');
    }
  }, [
    updateUserPreferencesApiStatus.isSuccess,
    clearAllUserPreferencesApiMessages,
    resetUpdateStatus,
    navigate,
    returnUrl,
    queryClient,
  ]);

  // If a user-initiated link fails, revert the opt-in so the user doesn't
  // save an opt-in that can't deliver (no resolved Slack ID). Gated on
  // surfaceLinkStatus so a failure from the automatic self-heal never
  // reverts state, even when the user has touched the checkbox meanwhile.
  useEffect(() => {
    if (linkSlackIdentityApiStatus.errorMessage && surfaceLinkStatus) {
      setProfileParams((prevState) => ({ ...prevState, slackAlert: false }));
    }
  }, [linkSlackIdentityApiStatus.errorMessage, surfaceLinkStatus]);

  // Self-heal: preferences can carry alertCenter.slack = true with no linked
  // identity (e.g. a best-effort save-time link that failed after the user
  // navigated away). Attempt the idempotent link once per visit so delivery
  // converges the next time the user opens their preferences.
  useEffect(() => {
    if (
      enableSlackNotifications &&
      !linkAttemptedRef.current &&
      userPreferences?.notificationSettings?.alertCenter?.slack === true &&
      !userPreferences.slackIdentity
    ) {
      linkAttemptedRef.current = true;
      linkSlackIdentity({ programId: getUserProgramId(), userId: getUserId() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot per visit, keyed on loaded preferences
  }, [userPreferences, enableSlackNotifications]);

  // Dismissing the flash bar clears messages and stops surfacing link
  // status until the next user-initiated attempt.
  const clearMessagesAndLinkStatusSurface = useCallback(() => {
    setSurfaceLinkStatus(false);
    clearAllUserPreferencesApiMessages();
  }, [clearAllUserPreferencesApiMessages]);

  const handleSlackAlertChange = (checked: boolean) => {
    slackAlertTouchedRef.current = true;
    setProfileParams((prevState) => ({ ...prevState, slackAlert: checked }));
    // Link the user's alias to their Slack ID on first opt-in. Idempotent and
    // inert until the preference is saved with alertCenter.slack = true.
    // Skips when a link (e.g. the silent self-heal) is already in flight.
    if (
      checked &&
      !userPreferences?.slackIdentity &&
      !linkSlackIdentityApiStatus.isLoading
    ) {
      linkAttemptedRef.current = true;
      setSurfaceLinkStatus(true);
      linkSlackIdentity({ programId: getUserProgramId(), userId: getUserId() });
    }
  };

  // A link attempt is only visible in the UI when it was user-initiated:
  // the silent self-heal must not disable the checkbox or show copy.
  const isUserLinkInFlight = surfaceLinkStatus && linkSlackIdentityApiStatus.isLoading;

  // Transient feedback only while a user-initiated link is in flight (the
  // checkbox is disabled meanwhile); no persistent linked-state copy.
  const renderSlackLinkStatus = () => {
    if (isUserLinkInFlight) {
      return (
        <Box color="text-body-secondary" fontSize="body-s">
          Linking your Slack account...
        </Box>
      );
    }
    return null;
  };

  const getLocationLinkVariant = (): 'primary' | 'secondary' => {
    return !profileParams.businessUnit || locationsList.length === 0
      ? 'secondary'
      : 'primary';
  };

  const createLocationPanel = () => (
    <LocationSelectionPanel
      selectedLocations={transformCodesToLocations(
        profileParams.locations || [],
        locationsList,
      )}
      onChange={(locations: Location[]) => {
        setProfileParams((prevState) => ({
          ...prevState,
          locations: extractCodes(locations),
        }));
      }}
      locations={locationsList}
    />
  );

  const renderFormContent = () => (
    <>
      {requiredBusinessUnitId && (
        <Container>
          <SpaceBetween direction="vertical" size="xs">
            <Box color="text-status-warning" fontSize="heading-s" fontWeight="bold">
              Switch to:{' '}
              {businessUnitList.find((bu) => bu.businessUnitId === requiredBusinessUnitId)
                ?.name || requiredBusinessUnitId}
            </Box>
            <Box color="text-body-secondary" fontSize="body-s">
              To view the shared link, select the business unit above and save your
              preferences. You can cancel to skip this.
            </Box>
          </SpaceBetween>
        </Container>
      )}
      <Container header={<Header variant="h2">Profile Settings</Header>}>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={<RequiredFieldLabel label="Business Unit" />}
            errorText={fieldValidationError.businessUnit}
          >
            <BusinessUnitListDropdown
              businessUnitList={businessUnitList}
              isLoading={fetchBusinessUnitApiStatus.isLoading}
              selectedBusinessUnit={businessUnitList.find(
                (item) => item.businessUnitId === profileParams.businessUnit,
              )}
              error={fetchBusinessUnitApiStatus.errorMessage}
              onBusinessUnitChange={(bu: BusinessUnit) => {
                setProfileParams((prevState) => ({
                  ...prevState,
                  businessUnit: bu?.businessUnitId,
                  locations: [],
                }));
              }}
            />
          </FormField>

          <FormField
            label={
              <RequiredFieldLabel label="Locations" required={locationsList.length > 0} />
            }
            errorText={fieldValidationError.locations}
          >
            <Link
              onFollow={() => {
                setTitle('Select Locations');
                setContent(createLocationPanel());
              }}
              variant={getLocationLinkVariant()}
            >
              Choose locations
            </Link>

            <TokenGroup
              onDismiss={
                isOpen
                  ? undefined
                  : ({ detail: { itemIndex } }) => {
                      setProfileParams((prev) => ({
                        ...prev,
                        locations: [
                          ...(prev.locations || []).slice(0, itemIndex),
                          ...(prev.locations || []).slice(itemIndex + 1),
                        ],
                      }));
                    }
              }
              limit={10}
              items={(profileParams.locations || []).map((code) => ({
                value: code,
                label: code,
                dismissLabel: isOpen ? undefined : `Remove ${code}`,
              }))}
            />
          </FormField>

          <FormField label="Language">
            <Select
              options={LANGUAGE_LIST}
              selectedOption={
                LANGUAGE_LIST.find((item) => item.value === profileParams?.language) ||
                LANGUAGE_LIST.find((item) => item.value === DEFAULT_LANGUAGE)!
              }
              onChange={({ detail }) => {
                setProfileParams((prevState) => ({
                  ...prevState,
                  language: detail.selectedOption?.value as string,
                }));
              }}
              placeholder="Choose a Language"
            />
          </FormField>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Notification Settings</Header>}>
        <ColumnLayout columns={2}>
          <FormField label="Alert me about">
            <SpaceBetween direction="vertical" size="xs">
              <Checkbox
                checked={profileParams.taskAlert}
                onChange={({ detail }) =>
                  setProfileParams((prevState) => ({
                    ...prevState,
                    taskAlert: detail.checked,
                  }))
                }
              >
                Task
              </Checkbox>
              <Checkbox
                checked={profileParams.auditAlert}
                onChange={({ detail }) =>
                  setProfileParams((prevState) => ({
                    ...prevState,
                    auditAlert: detail.checked,
                  }))
                }
              >
                Audit
              </Checkbox>
            </SpaceBetween>
          </FormField>

          <FormField label="Alert Center">
            <SpaceBetween direction="vertical" size="xs">
              <Checkbox
                checked={profileParams.emailAlert}
                onChange={({ detail }) =>
                  setProfileParams((prevState) => ({
                    ...prevState,
                    emailAlert: detail.checked,
                  }))
                }
              >
                Email
              </Checkbox>
              {enableSlackNotifications && (
                <>
                  <Checkbox
                    checked={profileParams.slackAlert}
                    disabled={isUserLinkInFlight}
                    onChange={({ detail }) => handleSlackAlertChange(detail.checked)}
                  >
                    Slack
                  </Checkbox>
                  {renderSlackLinkStatus()}
                </>
              )}
            </SpaceBetween>
          </FormField>
        </ColumnLayout>
      </Container>
    </>
  );

  const getBusinessUnitPreferences = () => {
    if (!profileParams.businessUnit) return undefined;

    const existing = userPreferences?.profileSettings?.businessUnitPreferences || [];
    const existingIndex = existing.findIndex(
      (item) => item.businessUnit === profileParams.businessUnit,
    );

    if (existingIndex >= 0) {
      return existing.map((item, index) =>
        index === existingIndex ? { ...item, locations: profileParams.locations } : item,
      );
    }

    return [
      ...existing,
      { businessUnit: profileParams.businessUnit, locations: profileParams.locations },
    ];
  };

  const handleSave = () => {
    if (!validate()) return;

    // Validate that required business unit is selected (if applicable)
    if (requiredBusinessUnitId && profileParams.businessUnit !== requiredBusinessUnitId) {
      setFieldValidationErrors({
        businessUnit: `You must select "${
          businessUnitList.find((bu) => bu.businessUnitId === requiredBusinessUnitId)
            ?.name || requiredBusinessUnitId
        }" to continue`,
      });
      return;
    }

    // Check if business unit is changing
    const oldBusinessUnit = userPreferences?.profileSettings?.businessUnit;
    const newBusinessUnit = profileParams.businessUnit;
    const isBusinessUnitChanging =
      oldBusinessUnit && newBusinessUnit && oldBusinessUnit !== newBusinessUnit;

    const updateData: UpdateUserPreferencesRequest = {
      programId: getUserProgramId(),
      userId: getUserId(),
      profileSettings: {
        businessUnit: profileParams.businessUnit,
        businessUnitPreferences: getBusinessUnitPreferences(),
        language: profileParams.language,
      },
      notificationSettings: {
        alertMeAbout: {
          task: profileParams.taskAlert,
          audit: profileParams.auditAlert,
        },
        alertCenter: {
          // Only persist a UI-driven Slack opt-in when the checkbox is
          // actually visible; with the flag off, round-trip the stored
          // value so an invisible control never invents an opt-in.
          slack: enableSlackNotifications
            ? profileParams.slackAlert
            : (userPreferences?.notificationSettings?.alertCenter?.slack ?? false),
          email: profileParams.emailAlert,
        },
      },
      lastUpdatedBy: getUserId(),
    };
    updateUserPreferences(updateData);

    // Best-effort link for users saving with Slack opted in but no identity
    // yet (e.g. first-time users whose opt-in defaulted to checked). If it
    // fails after navigation, the self-heal effect above retries on the
    // next visit to this page, so the stored opt-in converges to a linked
    // identity rather than silently never delivering.
    if (
      enableSlackNotifications &&
      profileParams.slackAlert &&
      !userPreferences?.slackIdentity &&
      !linkSlackIdentityApiStatus.isLoading
    ) {
      linkAttemptedRef.current = true;
      setSurfaceLinkStatus(true);
      linkSlackIdentity({ programId: getUserProgramId(), userId: getUserId() });
    }

    selectUserLanguage(DEFAULT_LANGUAGE);

    // Broadcast business unit change to other tabs (triggers page reload in App.tsx)
    if (isBusinessUnitChanging) {
      // NOTE: We do NOT clear filters for the old business unit
      // Filters are scoped per BU and should persist when switching back

      // Broadcast to other tabs
      localStorage.setItem(
        'businessUnitChanged',
        JSON.stringify({
          oldBusinessUnit,
          newBusinessUnit,
          timestamp: Date.now(),
        }),
      );
      // Remove the flag after a short delay so it can be used again
      setTimeout(() => {
        localStorage.removeItem('businessUnitChanged');
      }, 100);
    }
  };

  return (
    <ContentLayout>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: '800px', width: '100%' }}>
          <SpaceBetween direction="vertical" size="l">
            <ApiStatusFlashBar
              apiStatus={[
                updateUserPreferencesApiStatus,
                // Link status is only surfaced for user-initiated attempts;
                // the automatic self-heal link stays silent.
                ...(surfaceLinkStatus ? [linkSlackIdentityApiStatus] : []),
              ]}
              clearMessages={clearMessagesAndLinkStatusSurface}
            />

            {/* Logo and message section - for all users */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src={logoSrc} alt="Gravion Logo" style={logoStyles} />
              <div style={{ marginLeft: '24px' }}>
                {hasNoPreferences ? (
                  <>
                    <Box fontSize="heading-m" fontWeight="bold" color="text-label">
                      Hello, {getUserName() || getUserId()}
                    </Box>
                    <Box fontSize="heading-l" fontWeight="heavy" color="text-label">
                      Welcome to Gravion Audit Tool
                    </Box>
                    <Box fontSize="body-m" color="text-body-secondary">
                      Please select your profile preferences to get started using the tool
                    </Box>
                  </>
                ) : (
                  <>
                    <Box fontSize="heading-l" fontWeight="heavy" color="text-label">
                      Profile Preferences
                    </Box>
                    <Box fontSize="body-m" color="text-body-secondary">
                      Manage your preferences for the tool here
                    </Box>
                  </>
                )}
              </div>
            </div>

            {renderFormContent()}

            {/* Footer Message - only for first-time users */}
            {hasNoPreferences && (
              <Box textAlign="center" color="text-body-secondary" fontSize="body-m">
                You can update these preferences later from your profile menu
              </Box>
            )}

            {/* Action Buttons */}
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                {!hasNoPreferences && (
                  <Button variant="link" onClick={() => navigate('/')}>
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  loading={
                    updateUserPreferencesApiStatus.isLoading ||
                    getUserPreferencesApiStatus.isLoading
                  }
                  onClick={handleSave}
                >
                  Save Preferences
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </div>
      </div>
    </ContentLayout>
  );
};

export default ProfilePreferencesPage;
